import { useCallback, useEffect, useMemo, useState } from 'react';

import { type InfiniteData, type QueryClient, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import type { CommunityComment, CommunityCommentsResponse } from '~/interfaces/community/comment';

const COMMUNITY_COMMENTS_PATH = '/community/comments';

const communityCommentsKeys = {
  thread: (postId: string, commentId?: string) => ['community-comments', postId, commentId ?? null] as const,
  preview: (postId: string, previewCommentId: string) =>
    ['community-comments', postId, 'preview', previewCommentId] as const,
};

interface PageParam {
  cursor?: string;
  direction?: 'previous' | 'next';
}

const fetchCommentsPage = async (
  postId: string,
  pageParam: PageParam,
  commentId?: string,
): Promise<CommunityCommentsResponse> => {
  const params = new URLSearchParams({ post_id: postId });
  if (commentId) params.set('comment_id', commentId);
  if (pageParam.cursor) params.set('cursor', pageParam.cursor);
  if (pageParam.direction) params.set('direction', pageParam.direction);
  const response = await fetch(`${COMMUNITY_COMMENTS_PATH}?${params}`);
  if (!response.ok) throw new Error('Failed to fetch comments');
  return response.json();
};

const COMMENT_THREAD_STALE_TIME = 60_000;

// Starts a thread's first page before its comment UI mounts, under the key useCommentThread reads.
export const prefetchCommentThread = (queryClient: QueryClient, postId: string, commentId?: string) =>
  queryClient.prefetchInfiniteQuery({
    queryKey: communityCommentsKeys.thread(postId, commentId),
    queryFn: ({ pageParam }) => fetchCommentsPage(postId, pageParam, commentId),
    initialPageParam: {} as PageParam,
    staleTime: COMMENT_THREAD_STALE_TIME,
  });

export interface CommentThreadPreview {
  comment: CommunityComment;
  commentsCount: number;
}

interface UseCommentThreadOptions {
  commentId?: string;
  preview?: CommentThreadPreview;
  showFullThread?: boolean;
  onCommentDeleted?: () => void;
  onDeleteRollback?: () => void;
}

export interface UseCommentThreadReturn {
  comments: CommunityComment[];
  focusedCommentId: string | null;
  removedIds: Set<string>;
  hasMore: boolean;
  hasPrevious: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  isPreviewing: boolean;
  isLoadingFullThread: boolean;
  remainingCount: number;
  addComment: (comment: CommunityComment) => void;
  addReply: (parentId: string, reply: CommunityComment) => void;
  deleteComment: (commentId: string, options?: { reason?: string }) => Promise<boolean>;
  updateComment: (comment: CommunityComment) => void;
  loadMore: () => void;
  loadEarlier: () => void;
}

export const useCommentThread = (postId: string, options?: UseCommentThreadOptions): UseCommentThreadReturn => {
  const { commentId, preview, showFullThread = false, onCommentDeleted, onDeleteRollback } = options ?? {};
  const queryClient = useQueryClient();
  const deleteFetcher = useNonRevalidatingFetcher();
  const updateFetcher = useNonRevalidatingFetcher();
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  // Captured once at mount so a comment id arriving later (deep link hash) does not retroactively cancel the preview.
  const [hasCommentIdAtMount] = useState(() => Boolean(commentId));
  const previewedComment = showFullThread || hasCommentIdAtMount ? undefined : preview?.comment;

  const queryKey = previewedComment
    ? communityCommentsKeys.preview(postId, previewedComment.id)
    : communityCommentsKeys.thread(postId, commentId);

  const {
    data,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isFetching,
    isLoading,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchCommentsPage(postId, pageParam, commentId),
    initialPageParam: {} as PageParam,
    getNextPageParam: (lastPage) =>
      lastPage.meta.has_more && lastPage.meta.next_cursor
        ? { cursor: lastPage.meta.next_cursor, direction: 'next' as const }
        : undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage.meta.has_previous && firstPage.meta.previous_cursor
        ? { cursor: firstPage.meta.previous_cursor, direction: 'previous' as const }
        : undefined,
    staleTime: COMMENT_THREAD_STALE_TIME,
    enabled: !previewedComment,
    initialData: previewedComment ? buildPreviewData(previewedComment) : undefined,
  });

  const previewComment = preview?.comment;
  const comments = useMemo(
    () => data?.pages.flatMap((page) => page.comments) ?? (previewComment ? [previewComment] : []),
    [data, previewComment],
  );
  const focusedCommentId = useMemo(
    () => data?.pages.map((page) => page.focused_comment_id).find(Boolean) ?? null,
    [data],
  );

  // True from showing the full thread until its first page lands; stays false when cached data resolves the gap at once.
  const isLoadingFullThread = Boolean(preview) && !previewedComment && !data && isFetching;

  const previewCommentsCount = preview?.commentsCount;
  const remainingCount = useMemo(() => {
    if (previewCommentsCount === undefined) return 0;
    if (!previewedComment && !isLoadingFullThread) return 0;
    const loadedCount = comments.reduce((total, comment) => {
      const liveReplies = (comment.sub_comments ?? []).filter((sub) => !removedIds.has(sub.id)).length;
      return total + (removedIds.has(comment.id) ? 0 : 1) + liveReplies;
    }, 0);
    return Math.max(0, previewCommentsCount - loadedCount);
  }, [previewedComment, isLoadingFullThread, previewCommentsCount, comments, removedIds]);

  const addComment = useCallback(
    (newComment: CommunityComment) => {
      if (focusedCommentId && hasNextPage) return;

      queryClient.setQueryData<typeof data>(queryKey, (old) => {
        if (!old) return old;
        const lastPage = old.pages[old.pages.length - 1];
        return {
          ...old,
          pages: [...old.pages.slice(0, -1), { ...lastPage, comments: [...lastPage.comments, newComment] }],
        };
      });
    },
    [queryClient, queryKey, focusedCommentId, hasNextPage],
  );

  const addReply = useCallback(
    (parentId: string, reply: CommunityComment) => {
      queryClient.setQueryData<typeof data>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            comments: page.comments.map((c) => {
              if (c.id !== parentId) return c;
              return { ...c, sub_comments: [...(c.sub_comments || []), reply] };
            }),
          })),
        };
      });
    },
    [queryClient, queryKey],
  );

  const deleteComment = useCallback(
    async (commentId: string, options?: { reason?: string }) => {
      setRemovedIds((prev) => new Set(prev).add(commentId));
      onCommentDeleted?.();

      const formData = new FormData();
      formData.append('comment_id', commentId);
      const reason = options?.reason?.trim();
      if (reason) formData.append('reason', reason);

      const response = await deleteFetcher.submit(formData, { method: 'POST', action: '/delete_community_comment' });

      if (!response.ok) {
        setRemovedIds((prev) => {
          const next = new Set(prev);
          next.delete(commentId);
          return next;
        });
        onDeleteRollback?.();
        return false;
      }

      return true;
    },
    [deleteFetcher.submit, onCommentDeleted, onDeleteRollback],
  );

  const updateComment = useCallback(
    async (updated: CommunityComment) => {
      const snapshot = queryClient.getQueryData<typeof data>(queryKey);

      queryClient.setQueryData<typeof data>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            comments: page.comments.map((c) => {
              if (c.id === updated.id) {
                return { ...c, tiptap_state: updated.tiptap_state, updated_at: updated.updated_at };
              }
              if (c.sub_comments?.some((sc) => sc.id === updated.id)) {
                return {
                  ...c,
                  sub_comments: c.sub_comments.map((sc) =>
                    sc.id === updated.id
                      ? { ...sc, tiptap_state: updated.tiptap_state, updated_at: updated.updated_at }
                      : sc,
                  ),
                };
              }
              return c;
            }),
          })),
        };
      });

      const formData = new FormData();
      formData.append('comment_id', updated.id);
      formData.append('content_json', JSON.stringify(updated.tiptap_state));
      const response = await updateFetcher.submit(formData, { method: 'POST', action: '/update_community_comment' });

      if (!response.ok && snapshot) {
        queryClient.setQueryData(queryKey, snapshot);
      }
    },
    [updateFetcher.submit, queryClient, queryKey],
  );

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const loadEarlier = useCallback(() => {
    if (hasPreviousPage && !isFetchingPreviousPage) fetchPreviousPage();
  }, [fetchPreviousPage, hasPreviousPage, isFetchingPreviousPage]);

  const isShowingFullThreadOverPreview = Boolean(preview) && showFullThread;
  useEffect(() => {
    // Replies and edits made on the preview are already saved, so a cached thread must refetch to show them.
    if (isShowingFullThreadOverPreview) {
      queryClient.invalidateQueries({ queryKey: communityCommentsKeys.thread(postId, commentId) });
    }
  }, [isShowingFullThreadOverPreview, queryClient, postId, commentId]);

  return {
    comments,
    focusedCommentId,
    removedIds,
    hasMore: hasNextPage ?? false,
    hasPrevious: hasPreviousPage ?? false,
    isLoading,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isPreviewing: Boolean(previewedComment),
    isLoadingFullThread,
    remainingCount,
    addComment,
    addReply,
    deleteComment,
    updateComment,
    loadMore,
    loadEarlier,
  };
};

const buildPreviewData = (comment: CommunityComment): InfiniteData<CommunityCommentsResponse, PageParam> => ({
  pages: [
    {
      comments: [comment],
      focused_comment_id: null,
      meta: { has_more: false, next_cursor: null, limit: 1, has_previous: false, previous_cursor: null },
    },
  ],
  pageParams: [{}],
});
